import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { kitchenService } from '../services/kitchen';
import { Kitchen, CreateKitchenRequest } from '../types';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { useForm } from 'react-hook-form';
import { ConfirmationModal } from '../components/ConfirmationModal';
import {
  ChefHat,
  Plus,
  Search,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { PageSkeleton, EmptyState, ButtonLoader } from '../components/LoadingStates';

interface KitchenFormData {
  name: string;
}

export const Kitchens: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const activeRestaurantId = getActiveRestaurantId() || undefined;
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingKitchen, setEditingKitchen] = useState<Kitchen | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'warning' | 'danger' | 'info';
    confirmText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirm'
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<KitchenFormData>();

  // Fetch kitchens
  const { data: kitchens, isLoading } = useQuery(
    ['kitchens', user?.organizationCode, activeRestaurantId],
    () => kitchenService.getKitchens(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );

  // Create kitchen mutation
  const createMutation = useMutation(kitchenService.createKitchen, {
    onSuccess: () => {
      queryClient.invalidateQueries(['kitchens']);
      setIsCreateModalOpen(false);
      reset();
      toast.success('Kitchen created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create kitchen');
    }
  });

  // Update kitchen mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateKitchenRequest> }) =>
      kitchenService.updateKitchen(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kitchens']);
        setEditingKitchen(null);
        reset();
        toast.success('Kitchen updated successfully');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update kitchen');
      }
    }
  );

  // Toggle status mutation
  const toggleStatusMutation = useMutation(
    ({ id, active }: { id: string; active: boolean }) =>
      kitchenService.toggleKitchenStatus(id, active),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kitchens']);
        toast.success('Kitchen status updated');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update status');
      }
    }
  );

  // Delete kitchen mutation
  const deleteMutation = useMutation(kitchenService.deleteKitchen, {
    onSuccess: () => {
      queryClient.invalidateQueries(['kitchens']);
      toast.success('Kitchen deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete kitchen');
    }
  });

  const onSubmit = (data: KitchenFormData) => {
    if (!editingKitchen && !activeRestaurantId) {
      toast.error('Please select a restaurant before adding a kitchen.');
      return;
    }
    if (editingKitchen) {
      updateMutation.mutate({
        id: editingKitchen.id,
        data: { ...data, organizationCode: user?.organizationCode! }
      });
    } else {
      createMutation.mutate({
        ...data,
        organizationCode: user?.organizationCode!,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
      });
    }
  };

  const handleEdit = (kitchen: Kitchen) => {
    setEditingKitchen(kitchen);
    reset({ name: kitchen.name });
    setIsCreateModalOpen(true);
  };

  const handleDelete = (kitchen: Kitchen) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Kitchen',
      message: `Are you sure you want to delete "${kitchen.name}"? This action cannot be undone.`,
      onConfirm: () => {
        deleteMutation.mutate(kitchen.id);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: 'danger',
      confirmText: 'Delete'
    });
  };

  const handleToggleStatus = (kitchen: Kitchen) => {
    const action = kitchen.active ? 'deactivate' : 'activate';
    setConfirmationModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Kitchen`,
      message: `Are you sure you want to ${action} "${kitchen.name}"?`,
      onConfirm: () => {
        toggleStatusMutation.mutate({
          id: kitchen.id,
          active: !kitchen.active
        });
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: kitchen.active ? 'warning' : 'info',
      confirmText: action.charAt(0).toUpperCase() + action.slice(1)
    });
  };

  const filteredKitchens = kitchens?.filter(kitchen =>
    kitchen.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return <PageSkeleton type="grid" />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <ChefHat className="h-6 w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Kitchen Management
          </h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Manage your restaurant kitchens and stations</p>
        </div>
        <button
          onClick={() => {
            setEditingKitchen(null);
            reset();
            setIsCreateModalOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
          style={{ backgroundColor: currentTheme.colors.primary[600] }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Kitchen
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search kitchens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Kitchens Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredKitchens.map((kitchen) => (
          <div
            key={kitchen.id}
            className="rounded-xl border shadow-sm card-hover p-6 transition-all bg-white"
            style={{ borderColor: currentTheme.colors.border.primary }}
          >
            {/* Header with Kitchen Name and Status */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
                  <ChefHat className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                    {kitchen.name}
                  </h3>
                </div>
              </div>
              <span className={clsx(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                kitchen.active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              )}>
                {kitchen.active ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Kitchen Info */}
            {/* <div className="mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>
                  Organization:
                </span>
                <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  {kitchen.organizationCode}
                </span>
              </div>
            </div> */}
            
            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
              <button 
                onClick={() => handleToggleStatus(kitchen)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ 
                  backgroundColor: kitchen.active ? '#fef2f2' : '#f0fdf4',
                  color: kitchen.active ? '#dc2626' : '#16a34a',
                  border: `1px solid ${kitchen.active ? '#fecaca' : '#bbf7d0'}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = kitchen.active ? '#fee2e2' : '#dcfce7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = kitchen.active ? '#fef2f2' : '#f0fdf4';
                }}
                title={kitchen.active ? 'Click to deactivate kitchen' : 'Click to activate kitchen'}
              >
                {kitchen.active ? 'Make Inactive' : 'Make Active'}
              </button>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => handleEdit(kitchen)}
                  className="p-2 rounded-lg transition-colors"
                  style={{ 
                    color: currentTheme.colors.primary[600],
                    backgroundColor: currentTheme.colors.primary[50]
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[100]}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[50]}
                  title="Edit Kitchen"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(kitchen)}
                  className="p-2 rounded-lg transition-colors bg-red-50"
                  style={{ color: '#dc2626' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  title="Delete Kitchen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredKitchens.length === 0 && (
        <EmptyState
          icon={<ChefHat className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Kitchens Found"
          description={searchTerm ? 'Try adjusting your search.' : 'Get started by adding your first kitchen.'}
          action={
            !searchTerm ? (
              <button
                onClick={() => {
                  setEditingKitchen(null);
                  reset();
                  setIsCreateModalOpen(true);
                }}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Kitchen
              </button>
            ) : undefined
          }
        />
      )}

      {/* Create/Edit Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingKitchen ? 'Edit Kitchen' : 'Add New Kitchen'}
              </h2>
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setEditingKitchen(null);
                  reset();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kitchen Name
                </label>
                <input
                  {...register('name', { required: 'Kitchen name is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Main Kitchen, Beverages, Desserts"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingKitchen(null);
                    reset();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <ButtonLoader text={editingKitchen ? 'Updating...' : 'Creating...'} />
                  ) : (
                    editingKitchen ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        type={confirmationModal.type}
        isLoading={deleteMutation.isLoading || toggleStatusMutation.isLoading}
      />
    </div>
  );
};